import { useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { createClient } from '@supabase/supabase-js';

// Service client for privileged operations (bypasses RLS)
const supabaseService = createClient(
  import.meta.env.VITE_SUPABASE_URL,
  import.meta.env.VITE_SUPABASE_SERVICE_ROLE_KEY || import.meta.env.VITE_SUPABASE_ANON_KEY
);
import { edgeAIClient } from '@/integrations/ai/edge-client';
import { AIChatModeConfig } from './useAIChatModeConfig';

interface ExtractedField {
  name: string;
  value: string;
  confidence: number;
}

/**
 * Hook for AI data collection that integrates with existing PingPost system
 * Uses surgical approach to minimize changes to existing codebase
 */
export const useAIDataCollection = () => {

  /**
   * Extract data from AI conversation and integrate with trigger system
   */
  const processAIDataCollection = useCallback(async (
    conversationId: string,
    chatbotId: string,
    sessionId: string,
    config: AIChatModeConfig
  ) => {
    console.log('🔍 AI Data Collection - Starting process', {
      conversationId,
      chatbotId,
      sessionId,
      collectUserInfo: config.collectUserInfo,
      requiredFields: config.requiredFields,
      optionalFields: config.optionalFields
    });

    if (!config.collectUserInfo) {
      console.log('❌ AI Data Collection - collectUserInfo is false, skipping');
      return;
    }

    try {
      // Get conversation messages
      const { data: messages } = await supabase
        .from('messages')
        .select('content, type, created_at')
        .eq('conversation_id', conversationId)
        .order('created_at', { ascending: true });

      console.log('📝 AI Data Collection - Messages retrieved', {
        messageCount: messages?.length || 0,
        messages: messages?.map(m => ({ type: m.type, content: m.content.substring(0, 100) }))
      });

      if (!messages || messages.length === 0) {
        console.log('❌ AI Data Collection - No messages found');
        return;
      }

      // Extract data using AI
      const extractedData = await extractDataWithAI(messages, config, chatbotId);

      console.log('🤖 AI Data Collection - Data extracted', {
        extractedCount: extractedData.length,
        extractedData
      });

      if (extractedData.length === 0) {
        console.log('❌ AI Data Collection - No data extracted');
        return;
      }

      // Save as virtual user responses for trigger system
      try {
        console.log('🔄 [NEW CODE] About to call saveAsVirtualResponses', { extractedDataCount: extractedData.length, timestamp: new Date().toISOString() });
        await saveAsVirtualResponses(conversationId, chatbotId, extractedData);
        console.log('💾 AI Data Collection - Data saved as virtual responses');
      } catch (saveError) {
        console.error('❌ Error in saveAsVirtualResponses:', saveError);
      }

      // Check if we should trigger webhooks
      const allRequiredFields = config.requiredFields.map(f =>
        typeof f === 'string' ? f : f.name
      );

      const extractedFieldNames = extractedData.map(d => d.name);
      const hasAllRequired = allRequiredFields.every(field =>
        extractedFieldNames.includes(field)
      );

      console.log('🎯 AI Data Collection - Checking required fields', {
        allRequiredFields,
        extractedFieldNames,
        hasAllRequired
      });

      if (hasAllRequired) {
        // Trigger webhooks using existing system
        await triggerWebhooks(chatbotId, sessionId, conversationId, extractedData);
        console.log('🚀 AI Data Collection - Starting webhook trigger process', {
      chatbotId,
      sessionId,
      conversationId,
      extractedDataCount: extractedData.length
    });
      }

    } catch (error) {
      console.error('❌ Error processing AI data collection:', error);
    }
  }, []);

  return { processAIDataCollection };
};

/**
 * Extract structured data using AI (minimal implementation)
 */
async function extractDataWithAI(
  messages: any[],
  config: AIChatModeConfig,
  chatbotId: string
): Promise<ExtractedField[]> {
  const allFields = [
    ...config.requiredFields.map(f => typeof f === 'string' ? f : f.name),
    ...config.optionalFields.map(f => typeof f === 'string' ? f : f.name)
  ];

  console.log('🤖 AI Extraction - Starting', {
    allFields,
    messageCount: messages.length
  });

  if (allFields.length === 0) {
    console.log('❌ AI Extraction - No fields to extract');
    return [];
  }

  // Build conversation context
  const conversationText = messages
    .map(msg => `${msg.type === 'user' ? 'User' : 'Assistant'}: ${msg.content}`)
    .join('\n');

  // Create extraction prompt
  const prompt = `Extract information for these fields from the conversation:
Fields: ${allFields.join(', ')}

Conversation:
${conversationText}

Return JSON array: [{"name": "field_name", "value": "extracted_value", "confidence": 0.95}]
Only include fields with clear information. Confidence 0.0-1.0.`;

  console.log('🤖 AI Extraction - Prompt created', {
    promptLength: prompt.length,
    conversationLength: conversationText.length
  });

  try {
    console.log('🤖 AI Extraction - Calling chat-ai edge function directly');
    
    // Get Supabase credentials
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
    
    if (!supabaseUrl || !supabaseKey) {
      throw new Error('Supabase URL or key is missing');
    }
    
    // Call edge function directly
    const url = `${supabaseUrl}/functions/v1/chat-ai`;
    const edgeResponse = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${supabaseKey}`,
        'apikey': supabaseKey
      },
      body: JSON.stringify({
        message: 'Extract the data.',
        previousMessages: [
          { text: prompt, sender: 'system' }
        ],
        chatbotId
      })
    });
    
    if (!edgeResponse.ok) {
      const errorText = await edgeResponse.text();
      console.error(`Edge Function error: ${edgeResponse.status} ${errorText}`);
      throw new Error(`Edge Function error: ${edgeResponse.status} ${errorText}`);
    }
    
    const aiResponse = await edgeResponse.json();
    console.log('🤖 AI Extraction - EdgeAI response received', { aiResponse });
    const response = aiResponse.message;

    console.log('🤖 AI Extraction - Raw response', { response });

    // Handle markdown-wrapped JSON responses
    let jsonString = response;
    if (response.includes('```json')) {
      const jsonMatch = response.match(/```json\s*([\s\S]*?)\s*```/);
      if (jsonMatch) {
        jsonString = jsonMatch[1].trim();
      }
    } else if (response.includes('```')) {
      const codeMatch = response.match(/```\s*([\s\S]*?)\s*```/);
      if (codeMatch) {
        jsonString = codeMatch[1].trim();
      }
    }
    
    console.log('🤖 AI Extraction - Cleaned JSON string', { jsonString });
    const extracted = JSON.parse(jsonString) as ExtractedField[];
    const filtered = extracted.filter(d => d.confidence >= 0.7 && allFields.includes(d.name));

    console.log('🤖 AI Extraction - Parsed and filtered', {
      extracted,
      filtered,
      confidenceThreshold: 0.7
    });

    return filtered;
  } catch (error) {
    console.error('❌ AI extraction error:', error);
    return [];
  }
}

/**
 * Save extracted data as virtual user responses (hooks into existing system)
 */
async function saveAsVirtualResponses(
  conversationId: string,
  chatbotId: string,
  extractedData: ExtractedField[]
): Promise<void> {
  console.log('💾 Saving virtual responses', {
    conversationId,
    chatbotId,
    extractedDataCount: extractedData.length
  });

  // TEMPORARY FIX: Store extracted data in conversation metadata until RLS is fixed
  console.log('💾 Storing AI-extracted data in conversation metadata (RLS workaround)');
  
  try {
    // Create a summary object of extracted data
    const extractedDataSummary = {};
    for (const data of extractedData) {
      extractedDataSummary[data.name] = data.value;
      console.log('💾 Processing field', { fieldName: data.name, value: data.value });
    }

    // Store in conversation metadata using regular supabase client
    const { data: conversationUpdate, error: updateError } = await supabase
      .from('conversations')
      .update({
        metadata: {
          ai_extracted_data: extractedDataSummary,
          extracted_at: new Date().toISOString(),
          extraction_method: 'ai_chat_mode'
        }
      } as any)
      .eq('id', conversationId);

    if (updateError) {
      console.error('❌ Error storing AI data in conversation metadata:', updateError);
    } else {
      console.log('✅ AI-extracted data stored in conversation metadata', {
        conversationId,
        extractedFields: Object.keys(extractedDataSummary),
        data: extractedDataSummary
      });
    }

  } catch (metadataError) {
    console.error('❌ Error with metadata storage approach:', metadataError);
    
    // Fallback: Try to find existing questions and save responses
    console.log('💾 Fallback: Attempting to use existing questions');
    
    for (const data of extractedData) {
      console.log('💾 Processing field (fallback)', { fieldName: data.name, value: data.value });
      
      // Try to find existing questions by text pattern
      const { data: existingQuestions, error: searchError } = await supabase
        .from('questions')
        .select('id, text')
        .eq('chatbot_id', chatbotId)
        .ilike('text', `%${data.name}%`);
      
      if (existingQuestions && existingQuestions.length > 0) {
        const questionId = existingQuestions[0].id;
        console.log('💾 Found existing question', { fieldName: data.name, questionId });
        
        const { data: insertResult, error: insertError } = await supabase
          .from('user_responses')
          .insert({
            chatbot_id: chatbotId,
            conversation_id: conversationId,
            question_id: questionId,
            response_text: data.value
          } as any);
          
        if (insertError) {
          console.error('❌ Error saving to existing question:', insertError);
        } else {
          console.log('✅ Saved to existing question', { fieldName: data.name });
        }
      } else {
        console.log('⚠️ No existing question found for:', data.name);
      }
    }
  }
}

/**
 * Get or create virtual question for AI-extracted field
 */
async function getOrCreateVirtualQuestion(
  chatbotId: string,
  fieldName: string
): Promise<string | null> {
  const shortname = `ai_${fieldName}`;

  console.log('🔍 Getting/creating virtual question', { chatbotId, fieldName, shortname });

  // Check if exists by shortname (use service client to bypass RLS)
  const { data: existing, error: existingError } = await supabaseService
    .from('questions')
    .select('id')
    .eq('chatbot_id', chatbotId)
    .eq('shortname', shortname)
    .single();

  if (existingError && existingError.code !== 'PGRST116') {
    console.error('❌ Error checking existing question:', existingError);
  }

  if (existing) {
    console.log('✅ Found existing virtual question', { questionId: existing.id });
    return existing.id;
  }

  console.log('🆕 Creating new virtual question');

  // Create virtual question (use service client to bypass RLS)
  const { data: newQuestion, error: createError } = await supabaseService
    .from('questions')
    .insert({
      chatbot_id: chatbotId,
      text: `AI-extracted: ${fieldName}`,
      type: 'text',
      shortname,
      order: 9999,
      required: false
    } as any)
    .select('id')
    .single();

  if (createError) {
    console.error('❌ Error creating virtual question:', createError);
    return null;
  }

  console.log('✅ Created new virtual question', { questionId: newQuestion?.id });
  return newQuestion?.id || null;
}

/**
 * Trigger webhooks using existing trigger system
 */
async function triggerWebhooks(
  chatbotId: string,
  sessionId: string,
  conversationId: string,
  extractedData: ExtractedField[]
): Promise<void> {
  try {
    console.log('🎯 Trigger Webhooks - Fetching trigger rules', { chatbotId });
    
    // Get trigger rules
    const { data: triggerRules, error: rulesError } = await supabase
      .from('trigger_rules')
      .select('*')
      .eq('chatbot_id', chatbotId);

    if (rulesError) {
      console.error('❌ Error fetching trigger rules:', rulesError);
      return;
    }

    console.log('🎯 Trigger Webhooks - Rules fetched', {
      rulesCount: triggerRules?.length || 0,
      rules: triggerRules?.map(rule => ({ id: rule.id, name: rule.name, active: rule.active }))
    });

    if (!triggerRules || triggerRules.length === 0) {
      console.log('⚠️ Trigger Webhooks - No trigger rules found for chatbot');
      return;
    }

    // Build user responses for trigger evaluation
    const userResponses: Record<string, string> = {};
    extractedData.forEach(data => {
      // Use the format expected by trigger executor for data collection fields
      userResponses[`ai_extracted_${data.name}`] = data.value;
      // Also add the field name directly for backward compatibility
      userResponses[data.name] = data.value;
    });

    console.log('🎯 Trigger Webhooks - User responses prepared', {
      responseCount: Object.keys(userResponses).length,
      responses: userResponses
    });

    // Use existing trigger executor
    console.log('🎯 Trigger Webhooks - Calling trigger executor...');
    const { triggerExecutor } = await import('@/utils/triggerExecutor');
    await triggerExecutor.evaluateAndExecuteTriggers(triggerRules, {
      chatbotId,
      sessionId,
      conversationId,
      userResponses
    });
    
    console.log('✅ Trigger Webhooks - Trigger execution completed');

  } catch (error) {
    console.error('Error triggering webhooks:', error);
  }
}
